import { useEffect, useState } from 'react';
import { capturePostHogEvent, POSTHOG_KEY } from '../utils/analytics';
import {
  HOSTED_CHECKOUT_EXPERIMENT_NAME,
  HOSTED_CHECKOUT_FEATURE_FLAG,
} from '../utils/hostedCheckout';
import { isPreviewExperimentOverrideHost } from '../utils/experimentOverrideHosts';

const STORAGE_KEY = 'botspot:experiment:stripe-hosted-checkout-2026-08';
const VALID_VARIANTS = new Set(['control', 'stripe_hosted']);
const PRODUCTION_CHECKOUT_HOSTS = new Set(['botspot.trade', 'www.botspot.trade']);
const DEFAULT_DEPENDENCIES = {
  captureEvent: capturePostHogEvent,
  posthogKey: POSTHOG_KEY,
  isProductionCheckoutRuntime: () => PRODUCTION_CHECKOUT_HOSTS.has(window.location.hostname),
  getPreviewVariant: () => {
    if (!isPreviewExperimentOverrideHost()) return null;
    const value = new URLSearchParams(window.location.search).get('checkoutExperience');
    return VALID_VARIANTS.has(value) ? value : null;
  },
};

const newId = () => {
  if (window.crypto?.randomUUID) return `assign_${window.crypto.randomUUID()}`;
  return `assign_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

const isStringValue = (value) => value?.constructor === String;

const readAssignment = () => {
  try {
    const assignment = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    return assignment &&
      VALID_VARIANTS.has(assignment.variant) &&
      isStringValue(assignment.assignmentId) &&
      isStringValue(assignment.subjectId) &&
      isStringValue(assignment.exposedAt)
      ? assignment
      : null;
  } catch {
    return null;
  }
};

const writeAssignment = (assignment) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(assignment));
  } catch {
    // Assignment remains frozen in component state when storage is unavailable.
  }
};

const clearAssignment = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Treatment still fails closed in component state when storage is unavailable.
  }
};

const resolvePostHogFlag = () =>
  new Promise((resolve) => {
    let settled = false;
    let pollTimer;
    let timeoutId;
    let unsubscribe;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(pollTimer);
      window.clearTimeout(timeoutId);
      if (unsubscribe) unsubscribe();
      resolve(value);
    };
    const bind = () => {
      const client = window.posthog;
      if (!client?.getFeatureFlag) {
        pollTimer = window.setTimeout(bind, 50);
        return;
      }
      const immediateValue = client.getFeatureFlag(HOSTED_CHECKOUT_FEATURE_FLAG, {
        send_event: false,
      });
      if (VALID_VARIANTS.has(immediateValue) || immediateValue === false) {
        finish(immediateValue);
        return;
      }
      if (!client.onFeatureFlags) {
        finish(immediateValue);
        return;
      }
      unsubscribe = client.onFeatureFlags(() => {
        finish(
          client.getFeatureFlag(HOSTED_CHECKOUT_FEATURE_FLAG, {
            send_event: false,
          }),
        );
      });
      if (settled && unsubscribe) unsubscribe();
    };
    timeoutId = window.setTimeout(() => finish(null), 3000);
    bind();
  });

export default function useCheckoutExperienceExperiment(
  surface,
  dependencies = DEFAULT_DEPENDENCIES,
) {
  const {
    captureEvent,
    posthogKey,
    isProductionCheckoutRuntime = DEFAULT_DEPENDENCIES.isProductionCheckoutRuntime,
    getPreviewVariant = DEFAULT_DEPENDENCIES.getPreviewVariant,
  } = dependencies;
  const [result, setResult] = useState({
    variant: 'control',
    isLoading: true,
    enrolled: false,
    assignmentId: null,
  });

  useEffect(() => {
    let active = true;
    const previewVariant = getPreviewVariant();
    const cached = readAssignment();
    if (previewVariant) {
      clearAssignment();
      setResult({
        variant: previewVariant,
        isLoading: false,
        enrolled: true,
        assignmentId: `assign_preview_${previewVariant}`,
        subjectId: 'preview',
        exposedAt: new Date().toISOString(),
      });
      return () => {
        active = false;
      };
    }
    if (!posthogKey || !isProductionCheckoutRuntime()) {
      clearAssignment();
      setResult({ variant: 'control', isLoading: false, enrolled: false, assignmentId: null });
      return () => {
        active = false;
      };
    }

    void resolvePostHogFlag().then((rawVariant) => {
      if (!active) return;
      if (rawVariant === false) clearAssignment();
      if (!VALID_VARIANTS.has(rawVariant)) {
        setResult({ variant: 'control', isLoading: false, enrolled: false, assignmentId: null });
        return;
      }
      if (cached) {
        setResult({ ...cached, isLoading: false, enrolled: true });
        return;
      }
      const assignment = {
        variant: rawVariant,
        assignmentId: newId(),
        subjectId: window.posthog?.get_distinct_id?.() || 'anonymous',
        exposedAt: new Date().toISOString(),
      };
      writeAssignment(assignment);
      const eventProps = {
        experiment_name: HOSTED_CHECKOUT_EXPERIMENT_NAME,
        variant: assignment.variant,
        feature_flag: HOSTED_CHECKOUT_FEATURE_FLAG,
        feature_flag_value: assignment.variant,
        checkout_experience_assignment_id: assignment.assignmentId,
        checkout_experience_subject_id: assignment.subjectId,
        checkout_experience_surface: surface,
        checkout_experience_exposed_at: assignment.exposedAt,
        [`$feature/${HOSTED_CHECKOUT_FEATURE_FLAG}`]: assignment.variant,
        is_experiment_active: true,
      };
      captureEvent('$feature_flag_called', {
        ...eventProps,
        $feature_flag: HOSTED_CHECKOUT_FEATURE_FLAG,
        $feature_flag_response: assignment.variant,
      });
      captureEvent('experiment_exposure', eventProps);
      setResult({ ...assignment, isLoading: false, enrolled: true });
    });
    return () => {
      active = false;
    };
  }, [captureEvent, getPreviewVariant, isProductionCheckoutRuntime, posthogKey, surface]);

  return result;
}
